import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useFeedback } from '@/app/providers/FeedbackContext'

interface Props {
  children: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary component that catches React errors and displays a fallback UI.
 * Provides a "Report this error" button that opens the feedback modal with error context.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error!}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: Error
  errorInfo: ErrorInfo | null
  onReset: () => void
}

/**
 * Fallback UI shown when an error is caught.
 * This is a separate function component so it can use hooks.
 */
function ErrorFallback({ error, errorInfo, onReset }: ErrorFallbackProps) {
  const { openWithContext } = useFeedback()

  const handleReport = () => {
    openWithContext('error_report', {
      errorInfo: {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack ?? undefined,
      },
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="text-5xl mb-4">😵</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Something went wrong
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {error.message || 'An unexpected error occurred.'}
        </p>

        <div className="flex flex-col md:flex-row gap-3 justify-center">
          <button
            onClick={handleReport}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            Report this error
          </button>
          <button
            onClick={onReset}
            className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Try again
          </button>
        </div>

        <details className="mt-6 text-left">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
            Technical details
          </summary>
          <pre className="mt-2 p-3 bg-gray-100 rounded-lg text-xs text-gray-600 overflow-auto max-h-40">
            {error.stack || error.message}
          </pre>
        </details>
      </div>
    </div>
  )
}

export default ErrorBoundary
